import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./Settings.module.css";
import { FaPlay } from "react-icons/fa";
import { MdSubscriptions } from "react-icons/md";
import { IoIosCreate, IoMdLogIn } from "react-icons/io";
import { IoQrCodeOutline } from "react-icons/io5";
import { ImHome3 } from "react-icons/im";
import { BiSolidMoviePlay } from "react-icons/bi";
import { SiSteelseries } from "react-icons/si";
import { MdOutlineLiveTv } from "react-icons/md";
import { HiSparkles } from "react-icons/hi2";
import { MdContactMail } from "react-icons/md";
import { getSettingsSubscription, patchSettingsField } from "../../api/HttpApis";
import { checkAiAvailable } from "../../services/ai/aiAvailability";
import AppInfoSettings from "./AppInfoSettings";
import IntegrationSettings from "./IntegrationSettings";
import { APP_INFO_SECTIONS } from "./appInfoFields";
import { INTEGRATION_SECTIONS } from "./integrationFields";
import LegalSettings from "./LegalSettings";
import LanguageSettings from "./LanguageSettings";
import { LEGAL_KEYS, LEGAL_SECTIONS } from "./legalFields";

// Settings sub-menu. ?section=<key> keeps the choice on reload and makes each
// section linkable (e.g. #/mainlayout/settings?section=smtp).
const MENU_GROUPS = [
  { label: "General", items: [{ key: "general", title: "Feature toggles" }] },
  { label: "Site & app info", items: APP_INFO_SECTIONS.map(({ key, title }) => ({ key, title })) },
  { label: "Legal pages", items: LEGAL_SECTIONS.map(({ key, title }) => ({ key, title })) },
  { label: "Integrations", items: INTEGRATION_SECTIONS.map(({ key, title }) => ({ key, title })) },
];
const MENU_KEYS = MENU_GROUPS.flatMap((g) => g.items.map((i) => i.key));
const APP_INFO_KEYS = APP_INFO_SECTIONS.map((s) => s.key);

const Settings = () => {
  const [settingsConfig, setSettingsConfig] = useState({
    roku_pay_api_key: null,
    amazon_iap_secret: null,
    google_play_billing_key: null,
    enable_subscription: false,
    enable_ads: false,
    enable_qrlogin: false,
    enable_login: false,
    enable_home: false,
    enable_creator: false,
    enable_movie: false,
    enable_series: false,
    enable_live: false,
    enable_ai: false,
    enable_contact_us: true,
  });
  // Full /config row, for the site identity / app store / contact / social
  // sections (they keep their own form state).
  const [rawConfig, setRawConfig] = useState(null);
  // Why the AI toggle refused to turn on (empty = nothing to say).
  const [aiCheckMessage, setAiCheckMessage] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const activeSection = MENU_KEYS.includes(requested) ? requested : "general";
  const selectSection = (key) => setSearchParams(key === "general" ? {} : { section: key }, { replace: true });

  // Sub-menu dots for sections with unsaved edits.
  const [appInfoDirty, setAppInfoDirty] = useState([]);
  const [integrationDirty, setIntegrationDirty] = useState({});
  const onIntegrationDirty = useCallback(
    (key, dirty) => setIntegrationDirty((prev) => (prev[key] === dirty ? prev : { ...prev, [key]: dirty })),
    []
  );
  const [legalDirty, setLegalDirty] = useState([]);
  const [languageDirty, setLanguageDirty] = useState(false);
  const isDirty = (key) =>
    appInfoDirty.includes(key) ||
    legalDirty.includes(key) ||
    Boolean(integrationDirty[key]) ||
    (key === "general" && languageDirty);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getSettingsSubscription();
        const config = response?.data;
        setRawConfig(config ?? null);

        setSettingsConfig({
          roku_pay_api_key: config?.roku_pay_api_key ?? null,
          amazon_iap_secret: config?.amazon_iap_secret ?? null,
          google_play_billing_key: config?.google_play_billing_key ?? null,
          enable_subscription: config?.enable_subscription ?? false,
          enable_ads: config?.enable_ads ?? false,
          enable_qrlogin: config?.enable_qrlogin ?? false,
          enable_login: config?.enable_login ?? false,
          enable_home: config?.enable_home ?? false,
          enable_creator: config?.enable_creator ?? false,
          enable_movie: config?.enable_movie ?? false,
          enable_series: config?.enable_series ?? false,
          enable_live: config?.enable_live ?? false,
          enable_ai: config?.enable_ai ?? false,
          enable_contact_us: config?.enable_contact_us ?? true,
        });
      } catch (error) {
        console.error("Failed to fetch settings config:", error);
      }
    };

    fetchConfig();
  }, []);

  const handleToggle = async (key) => {
    const updatedValue = !settingsConfig[key];

    // Turning AI ON requires a reachable analyze server with a Gemini key —
    // otherwise the operator would get AI buttons that fail on click. Switching
    // it OFF is always allowed, so a broken AI server can still be disabled.
    if (key === "enable_ai" && updatedValue) {
      setAiCheckMessage("Checking AI server…");
      const { available, reason } = await checkAiAvailable();
      if (!available) {
        setAiCheckMessage(`${reason} AI cannot be enabled.`);
        return;
      }
      setAiCheckMessage("");
    }

    setSettingsConfig((prev) => ({
      ...prev,
      [key]: updatedValue,
    }));

    try {
      await patchSettingsField({
        field: key,
        value: updatedValue,
      });
    } catch (error) {
      console.error(`Failed to update ${key}:`, error);

      setSettingsConfig((prev) => ({
        ...prev,
        [key]: !updatedValue,
      }));
    }
  };

  return (
    <div className={styles.innerContainer}>
      <h2 className={styles.title}>Settings</h2>
      <div className={styles.settingsLayout}>
        <nav className={styles.subMenu} aria-label="Settings sections">
          {MENU_GROUPS.map((group) => (
            <div key={group.label} className={styles.subMenuGroup}>
              <div className={styles.subMenuLabel}>{group.label}</div>
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`${styles.subMenuItem} ${item.key === activeSection ? styles.subMenuItemActive : ""}`}
                  aria-current={item.key === activeSection ? "page" : undefined}
                  onClick={() => selectSection(item.key)}
                >
                  {item.title}
                  {isDirty(item.key) && <span className={styles.dirtyDot} aria-label="unsaved changes" />}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.subContent}>
          <div className={styles.adCards} hidden={activeSection !== "general"}>
            {/* Enable Subscription */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <MdSubscriptions className={styles.icon} />
                  <div>
                    <h4>Enable Subscription</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_subscription}
                      onChange={() => handleToggle("enable_subscription")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable Ads */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <FaPlay className={styles.icon} />
                  <div>
                    <h4>Enable Ads</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_ads}
                      onChange={() => handleToggle("enable_ads")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable QR Login */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <IoQrCodeOutline className={styles.icon} />
                  <div>
                    <h4>Enable QR Login</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_qrlogin}
                      onChange={() => handleToggle("enable_qrlogin")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable Login */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <IoMdLogIn className={styles.icon} />
                  <div>
                    <h4>Enable Login</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_login}
                      onChange={() => handleToggle("enable_login")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable Home */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <ImHome3 className={styles.icon} />
                  <div>
                    <h4>Enable Home</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_home}
                      onChange={() => handleToggle("enable_home")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable Creator */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <IoIosCreate className={styles.icon} />
                  <div>
                    <h4>Enable Creator</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_creator}
                      onChange={() => handleToggle("enable_creator")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable Movie */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <BiSolidMoviePlay className={styles.icon} />
                  <div>
                    <h4>Enable Movie</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_movie}
                      onChange={() => handleToggle("enable_movie")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable Series */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <SiSteelseries className={styles.icon} />
                  <div>
                    <h4>Enable Series</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_series}
                      onChange={() => handleToggle("enable_series")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable Live */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <MdOutlineLiveTv className={styles.icon} />
                  <div>
                    <h4>Enable Live</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_live}
                      onChange={() => handleToggle("enable_live")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Enable AI — master switch for the whole AI interface (AI Studio,
                metadata, subtitles, dubbing, trailers, scrubbing previews).
                Requires a Gemini key + a running analyze server; leave it off for
                clients that have neither. Takes effect on reload. */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <HiSparkles className={styles.icon} />
                  <div>
                    <h4>Enable AI</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_ai}
                      onChange={() => handleToggle("enable_ai")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
              {aiCheckMessage && (
                <p style={{ margin: "8px 0 0", fontSize: 12, opacity: 0.75 }}>
                  {aiCheckMessage}
                </p>
              )}
            </div>

            {/* Website Contact Us page + its footer link. */}
            <div className={styles.card}>
              <div className={styles.header}>
                <div className={styles.iconContainer}>
                  <MdContactMail className={styles.icon} />
                  <div>
                    <h4>Show Contact Us Page</h4>
                  </div>
                </div>
                <div>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={settingsConfig.enable_contact_us}
                      onChange={() => handleToggle("enable_contact_us")}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>
            {/* Language Support -- the website's footer language selector.
                Toggle + languages + default saved together (own Save button). */}
            <LanguageSettings config={rawConfig} onDirtyChange={setLanguageDirty} />
          </div>

          {/* Kept mounted while hidden so unsaved edits survive switching sections. */}
          <AppInfoSettings
            config={rawConfig}
            sectionKey={APP_INFO_KEYS.includes(activeSection) ? activeSection : null}
            onDirtyChange={setAppInfoDirty}
          />
          {/* Same: one form per legal document, kept mounted with its edits. */}
          <LegalSettings
            sectionKey={LEGAL_KEYS.includes(activeSection) ? activeSection : null}
            onDirtyChange={setLegalDirty}
          />
          {INTEGRATION_SECTIONS.map((section) => (
            <IntegrationSettings
              key={section.key}
              section={section}
              hidden={section.key !== activeSection}
              onDirtyChange={onIntegrationDirty}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Settings;
